import { useCallback } from 'react';
import { requisitionsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Requisition } from '../types';

export function useRequisitions(
  options?: ResourceQueryOptions
): ResourceState<Requisition> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      requisitionsService.getAll(opts) as unknown as Promise<{ data: Requisition[]; meta?: any }>,
    []
  );
  return useResourceCollection<Requisition>(fetchFn, options);
}

export function useRequisition(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Requisition> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (requisitionId: number, opts?: ResourceQueryOptions) =>
      requisitionsService.getById(requisitionId, opts) as unknown as Promise<Requisition>,
    []
  );
  return useResourceById<Requisition>(fetchFn, numericId, options);
}

export function useCreateRequisition(
  onSuccess?: (data: Requisition) => void
): MutationState<Requisition> {
  const createFn = useCallback(
    (data: any) => requisitionsService.create(data) as unknown as Promise<Requisition>,
    []
  );
  return useCreateResource<Requisition>(createFn, onSuccess);
}

export function useUpdateRequisition(
  onSuccess?: (data: Requisition) => void
): MutationState<Requisition> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return requisitionsService.update(numericId, data) as unknown as Promise<Requisition>;
    },
    []
  );
  return useUpdateResource<Requisition>(updateFn, onSuccess);
}

/**
 * Delete requisition using documentId
 * @param data - Full requisition object containing documentId
 */
export function useDeleteRequisition(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return requisitionsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

